import { ExternalLink } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table';
import type { InvoiceRecord } from '@/api-types';

interface InvoiceHistoryProps {
	invoices: InvoiceRecord[];
}

function formatDate(ts: number | null): string {
	if (!ts) return '--';
	return new Date(ts * 1000).toLocaleDateString();
}

function formatAmount(cents: number, currency: string): string {
	return new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency: currency.toUpperCase(),
	}).format(cents / 100);
}

function statusVariant(status: string): 'default' | 'secondary' | 'destructive' | 'outline' {
	switch (status) {
		case 'paid': return 'default';
		case 'open': return 'secondary';
		case 'void':
		case 'uncollectible': return 'destructive';
		default: return 'outline';
	}
}

export function InvoiceHistory({ invoices }: InvoiceHistoryProps) {
	if (invoices.length === 0) {
		return <p className="text-sm text-text-tertiary">No invoices yet.</p>;
	}

	return (
		<Table>
			<TableHeader>
				<TableRow>
					<TableHead>Date</TableHead>
					<TableHead>Period</TableHead>
					<TableHead>Amount</TableHead>
					<TableHead>Status</TableHead>
					<TableHead className="text-right">Invoice</TableHead>
				</TableRow>
			</TableHeader>
			<TableBody>
				{invoices.map((inv) => (
					<TableRow key={inv.id}>
						<TableCell className="text-sm">{formatDate(inv.created_at)}</TableCell>
						<TableCell className="text-sm text-text-secondary">
							{formatDate(inv.period_start)} - {formatDate(inv.period_end)}
						</TableCell>
						<TableCell className="text-sm font-medium">
							{formatAmount(inv.amount_cents, inv.currency)}
						</TableCell>
						<TableCell>
							<Badge variant={statusVariant(inv.status)}>{inv.status}</Badge>
						</TableCell>
						<TableCell className="text-right">
							{inv.invoice_url && (
								<Button variant="ghost" size="sm" asChild>
									<a href={inv.invoice_url} target="_blank" rel="noopener noreferrer">
										<ExternalLink className="h-4 w-4" />
									</a>
								</Button>
							)}
						</TableCell>
					</TableRow>
				))}
			</TableBody>
		</Table>
	);
}
